import React, { useState, useEffect, useCallback } from 'react';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';
import styled from 'styled-components';
import { useToast } from '../Context/ToastContext';

/**
 * TeachProgramPage — self-serve "teach the brain a program from a document".
 *
 * Adviser pastes an OFFICIAL program document → AI (source-anchored) proposes a pathway of
 * discovery questions → adviser reviews/edits → saves into their own brain. Because the brain
 * uses overlay inheritance, the saved pathway layers on top of the shared best-brain base, so
 * the widget immediately starts asking these program-specific questions.
 *
 * Backend: /program-knowledge/{from-document,save,custom}
 */

const DOMAINS = [
  { value: 'provincial-nominee-programs', label: 'Provincial Nominee Programs' },
  { value: 'express-entry', label: 'Express Entry' },
  { value: 'work-permits', label: 'Work Permits' },
  { value: 'study-permits', label: 'Study Permits' },
  { value: 'family-sponsorship', label: 'Family Sponsorship' },
  { value: 'business-immigration', label: 'Business Immigration' },
  { value: 'citizenship', label: 'Citizenship' },
  { value: 'custom-programs', label: 'Custom Programs' },
];

export default function TeachProgramPage() {
  const axios = useAuthenticatedAxios();
  const toast = useToast();

  const [programName, setProgramName] = useState('');
  const [text, setText] = useState('');
  const [domain, setDomain] = useState('provincial-nominee-programs');
  const [draft, setDraft] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customList, setCustomList] = useState([]);

  const loadCustom = useCallback(async () => {
    try {
      const res = await axios.get('/program-knowledge/custom');
      setCustomList(res.data?.pathways || []);
    } catch (e) { /* non-fatal */ }
  }, [axios]);

  useEffect(() => { loadCustom(); }, [loadCustom]);

  const generate = async () => {
    if (text.trim().length < 40) { toast?.error?.('Paste the official program text first (a few sentences).'); return; }
    setGenerating(true);
    try {
      const res = await axios.post('/program-knowledge/from-document', { text, programName });
      const p = res.data?.pathway;
      setDraft(p);
      if (DOMAINS.some(d => d.value === p?.category)) setDomain(p.category);
      toast?.success?.('Draft generated — review and edit the questions before saving.');
    } catch (e) {
      const msg = e?.response?.data?.message || 'Failed to generate. Try shortening the text.';
      toast?.error?.(msg);
    } finally { setGenerating(false); }
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const res = await axios.post('/program-knowledge/save', { pathway: draft, domain });
      toast?.success?.(`Saved "${draft.displayName}" — the widget will now ask its ${res.data?.questionCount} questions.`);
      setDraft(null); setText(''); setProgramName('');
      loadCustom();
    } catch (e) {
      toast?.error?.(e?.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const removeCustom = async (id, dom) => {
    try {
      await axios.delete(`/program-knowledge/custom/${encodeURIComponent(id)}?domain=${encodeURIComponent(dom)}`);
      toast?.success?.('Removed.');
      loadCustom();
    } catch (e) { toast?.error?.('Failed to remove.'); }
  };

  // Editing helpers on the draft
  const editField = (field, value) => setDraft(d => ({ ...d, [field]: value }));
  const editItem = (bucket, idx, value) =>
    setDraft(d => ({ ...d, [bucket]: d[bucket].map((it, i) => i === idx ? { ...it, discoveryQuestion: value } : it) }));
  const removeItem = (bucket, idx) =>
    setDraft(d => ({ ...d, [bucket]: d[bucket].filter((_, i) => i !== idx) }));

  const totalQuestions = draft
    ? (draft.requirements?.length || 0) + (draft.blockers?.length || 0) + (draft.unlockFactors?.length || 0)
    : 0;

  const renderBucket = (bucket, title, hint) => {
    const items = draft?.[bucket] || [];
    if (!items.length) return null;
    return (
      <Bucket>
        <BucketTitle>{title} <Muted>({items.length}) — {hint}</Muted></BucketTitle>
        {items.map((it, i) => (
          <QRow key={it.id || i}>
            <QInput value={it.discoveryQuestion} onChange={e => editItem(bucket, i, e.target.value)} />
            <RemoveBtn title="Remove question" onClick={() => removeItem(bucket, i)}>✕</RemoveBtn>
          </QRow>
        ))}
      </Bucket>
    );
  };

  return (
    <PageWrapper>
      <Container>
        <H1>Teach the brain a program</H1>
        <Lead>
          Paste an <b>official</b> program page (e.g. an OINP stream). The assistant structures
          only what's written into questions your widget will ask leads — it never invents rules.
          Review and edit before saving.
        </Lead>

        {!draft && (
          <Card>
            <Label>Program name (optional)</Label>
            <TextInput placeholder="e.g. Ontario Workforce Priority stream"
              value={programName} onChange={e => setProgramName(e.target.value)} />
            <Label>Official document text</Label>
            <TextArea rows={12} placeholder="Paste the official program page text here…"
              value={text} onChange={e => setText(e.target.value)} />
            <Row>
              <PrimaryBtn disabled={generating} onClick={generate}>
                {generating ? 'Generating…' : 'Generate questions'}
              </PrimaryBtn>
              <Muted>{text.length} chars</Muted>
            </Row>
          </Card>
        )}

        {draft && (
          <Card>
            <Label>Program</Label>
            <TextInput value={draft.displayName} onChange={e => editField('displayName', e.target.value)} />
            <Label>Summary</Label>
            <TextArea rows={3} value={draft.description} onChange={e => editField('description', e.target.value)} />
            <Row>
              <div>
                <Label>Save under category</Label>
                <Select value={domain} onChange={e => setDomain(e.target.value)}>
                  {DOMAINS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </Select>
              </div>
              <Muted style={{ alignSelf: 'flex-end' }}>{totalQuestions} questions the widget will ask</Muted>
            </Row>

            {renderBucket('requirements', 'Requirement questions', 'confirm eligibility')}
            {renderBucket('blockers', 'Blocker questions', 'detect disqualifiers')}
            {renderBucket('unlockFactors', 'Unlock-factor questions', 'find advantages')}

            <Row>
              <PrimaryBtn disabled={saving || totalQuestions === 0} onClick={save}>
                {saving ? 'Saving…' : 'Save to my brain'}
              </PrimaryBtn>
              <SecondaryBtn onClick={() => setDraft(null)}>Discard</SecondaryBtn>
            </Row>
          </Card>
        )}

        <H2>Your programs</H2>
        {customList.length === 0 && <Muted>No self-authored programs yet.</Muted>}
        {customList.map(p => (
          <ListRow key={p.id}>
            <div>
              <b>{p.displayName}</b> <Muted>· {p.domain} · {p.questionCount} questions {p.isActive ? '' : '· inactive'}</Muted>
            </div>
            <SecondaryBtn onClick={() => removeCustom(p.id, p.domain)}>Remove</SecondaryBtn>
          </ListRow>
        ))}
      </Container>
    </PageWrapper>
  );
}

// ── styles ────────────────────────────────────────────────────────────────
const PageWrapper = styled.div`
  display: flex; justify-content: center; width: 100%; height: 100%;
  background: linear-gradient(135deg, #0a0d12 0%, #111820 50%, #0d1117 100%);
  padding: 40px; box-sizing: border-box; overflow-y: auto;
`;
const Container = styled.div`width: 100%; max-width: 900px; color: #fff; padding-bottom: 60px;`;
const H1 = styled.h1`font-size: 26px; margin: 0 0 8px;`;
const H2 = styled.h2`font-size: 18px; margin: 32px 0 12px;`;
const Lead = styled.p`color: #9fb0c0; line-height: 1.5; margin: 0 0 20px; max-width: 680px;`;
const Card = styled.div`
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 14px; padding: 20px; margin-bottom: 20px;
`;
const Label = styled.div`font-size: 13px; color: #8aa0b4; margin: 14px 0 6px;`;
const inputBase = `
  width: 100%; box-sizing: border-box; background: rgba(0,0,0,0.35);
  border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; color: #fff;
  padding: 10px 12px; font-size: 14px; outline: none;
  &:focus { border-color: #4f8cff; }
`;
const TextInput = styled.input`${inputBase}`;
const TextArea = styled.textarea`${inputBase} resize: vertical; font-family: inherit;`;
const Select = styled.select`${inputBase} width: auto; min-width: 240px;`;
const Row = styled.div`display: flex; gap: 12px; align-items: center; margin-top: 16px; flex-wrap: wrap;`;
const PrimaryBtn = styled.button`
  background: linear-gradient(135deg, #4f8cff, #2f6fe0); color: #fff; border: none;
  border-radius: 8px; padding: 11px 20px; font-size: 14px; font-weight: 600; cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
const SecondaryBtn = styled.button`
  background: rgba(255,255,255,0.06); color: #cdd9e5; border: 1px solid rgba(255,255,255,0.14);
  border-radius: 8px; padding: 10px 16px; font-size: 14px; cursor: pointer;
`;
const Muted = styled.span`color: #7d8fa1; font-size: 13px;`;
const Bucket = styled.div`margin-top: 18px;`;
const BucketTitle = styled.div`font-size: 14px; font-weight: 600; margin-bottom: 8px;`;
const QRow = styled.div`display: flex; gap: 8px; margin-bottom: 8px; align-items: center;`;
const QInput = styled.input`${inputBase}`;
const RemoveBtn = styled.button`
  background: rgba(255,80,80,0.12); color: #ff8a8a; border: 1px solid rgba(255,80,80,0.3);
  border-radius: 8px; padding: 8px 12px; cursor: pointer; flex-shrink: 0;
`;
const ListRow = styled.div`
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07);
  border-radius: 10px; padding: 12px 16px; margin-bottom: 8px;
`;
